import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { yupToFormErrors, Formik, Form } from 'formik';
import { observer } from 'mobx-react';
import { rejectCodes, lookupsJson } from '@ourbranch/lookups';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';
import { Grid } from '@material-ui/core';
import { useHistory } from 'react-router-dom';

import withDatePicker from 'core/components/with-date-picker';
import { Button } from 'core/components/button';
import BaseDialog from 'core/components/base-dialog';
import { Label } from 'core/components/label';
import { useStore } from 'core/store';
import { AuthContext } from 'core/components/auth';
import { useToast } from 'core/components/toast';
import Field from 'core/components/form/form.v2';
import { mapOfferInputToQuoteRequestInput } from 'core/helpers/quoter.service';
import { types } from 'core/helpers/sanitize';
import { isMissingPrimaryFarmOperations } from 'common/helpers/farm-liability-precheck';
import { Home } from '../../../../../../quote/components/property-detail/home';
import { vinSchema, homeSchema, priorCoverageSchema } from './validation-schema';
import { InitialRiskDetail } from '../../../../../../quote/components/initial-risk-detail';

import useStyles from './resubmit-offer-modal.styles';

const fixableAutoError = rejectCodes.NO_CARS;
const fixableHomeError = rejectCodes.VERISK_NO360;

// Errors that should update the existing offer instead of creating a new quote
const updateOfferErrors = [rejectCodes.NEED_PRIOR_AUTO_COVERAGE_DETAILS];

const ResubmitOfferModal = ({ rejectionData, onClose }) => {
  const classes = useStyles();
  const history = useHistory();
  const session = useContext(AuthContext);
  const toast = useToast();
  const { offer: offerStore, quote: quoteStore } = useStore();
  const [loading, setLoading] = useState(false);

  const isNoCarsError = rejectionData.code === fixableAutoError;
  const isHomeError = rejectionData.code === fixableHomeError;
  const isPriorCoverageError = rejectionData.code === rejectCodes.NEED_PRIOR_AUTO_COVERAGE_DETAILS;

  const isUpdateOfferError = updateOfferErrors.includes(rejectionData.code);

  // Check if this is a farm policy
  const isFarmPolicy = isFarmBasedPolicyType(offerStore?.offer?.quote?.policyType);

  const baseInitialValues = {
    ...offerStore?.offer?.quote,
    // Ensure drivers have non-null driversLicenseNumber to prevent trim() errors
    drivers:
      offerStore?.offer?.quote?.drivers?.map((driver) => ({
        ...driver,
        driversLicenseNumber: driver.driversLicenseNumber || ''
      })) || []
  };

  if (isNoCarsError) {
    baseInitialValues.VIN = '';
  }

  if (isPriorCoverageError) {
    baseInitialValues.global = {
      ...offerStore?.offer?.quote?.global,
      currentAutoCarrier: '',
      continuousAutoCoverageStartDate: '',
      mostRecentAutoInsuranceCoverageEndDate: '',
      priorIndividualBILimit: '',
      priorOccurrenceBILimit: '',
      priorUmIndividualBodilyInjury: offerStore?.offer?.quote?.global?.priorUmIndividualBodilyInjury || '',
      priorUmOccurrenceBodilyInjury: offerStore?.offer?.quote?.global?.priorUmOccurrenceBodilyInjury || ''
    };
    // Set default autoCoverage.policyLimitBIPD if not present - required by rater to clear 50099
    // Default '50/100/50' matches packages/rater/src/retrieve-data-auto.js:387
    baseInitialValues.autoCoverage = {
      ...offerStore?.offer?.quote?.autoCoverage,
      policyLimitBIPD: offerStore?.offer?.quote?.autoCoverage?.policyLimitBIPD || '50/100/50'
    };
  }

  if (isHomeError && !isFarmPolicy) {
    baseInitialValues.home = {
      basementSqFt: '',
      basementType: '',
      constructionType: '',
      exteriorWallType: '',
      garageCarCapacity: '',
      garageType: '',
      homeQuality: '',
      numFamiliesInDwelling: '',
      numFireplaces: '',
      numFullBathrooms: '',
      numHalfBathrooms: '',
      numStories: '',
      numWoodBurningStoves: '',
      purchaseDate: '',
      roofShape: '',
      roofType: '',
      roofYear: '',
      sqFt: '',
      typeOfHome: '',
      yearBuilt: '',
      ...offerStore?.offer?.quote?.home
    };
  }

  const initialValues = baseInitialValues;

  const validate = async (modalValues) => {
    try {
      if (isNoCarsError) {
        await vinSchema.validate(modalValues, { abortEarly: false });
      } else if (isPriorCoverageError) {
        await priorCoverageSchema.validate(modalValues, { abortEarly: false });
      } else if (isHomeError && !isFarmPolicy) {
        await homeSchema.validate(modalValues, { abortEarly: false });
      }
      // Farm-specific recalc preconditions (e.g. #31372's `farmOperations`)
      // are enforced inline in `onUpdate` rather than via a Yup schema here,
      // since this modal only renders auto/home rejection fields.
    } catch (error) {
      const formErrors = yupToFormErrors(error);
      console.log({ formErrors });

      return formErrors;
    }
  };

  //resubmits the quote from scratch
  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const mappedInput = mapOfferInputToQuoteRequestInput(values);
      const newOfferId = await quoteStore.requestQuoteAction(mappedInput, session.allStates, true);
      if (newOfferId) {
        history.push(`/offer/${newOfferId}`);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error during requote:', error);
      offerStore.handleError(error);
      setLoading(false);
    }
  };

  //updates the quote just like the update offer in the footer and clears the rejection
  const onUpdate = async (values) => {
    try {
      setLoading(true);

      // Issue #31372: this modal's `validate` skips the shared farm schema, so a
      // farm-bundled offer with `includeFarmLiability: true` and an empty
      // `farmOperations` would hit ClarionDoor here and 5042. Mirror the
      // offer-page Yup rule (PR #31354) as a precondition.
      if (isFarmPolicy && isMissingPrimaryFarmOperations(values)) {
        toast.notify({
          type: 'error',
          message: 'Primary farm operations is required on the Liability section before updating this offer.'
        });
        setLoading(false);
        onClose();
        return;
      }

      const formattedDetails = types.QuoteDetailsInput(values);
      // Signal to the UW rules that staff explicitly submitted prior coverage via this modal,
      // so the 50099 rejection can be safely cleared (CV-populated fields alone are not sufficient).
      formattedDetails.global = { ...formattedDetails.global, staffProvidedPriorCoverage: true };
      await offerStore.recalculateQuoteToCluster(offerStore.offer.id, formattedDetails, history);
      onClose();
    } catch (error) {
      console.error('Error during update:', error);
      offerStore.handleError(error);
      setLoading(false);
    }
  };

  return (
    <BaseDialog size="lg" onClose={onClose} open>
      <Grid className={classes.container}>
        <Label className={classes.mainLabel} type="greenBig">
          {rejectionData.modalText}
        </Label>

        <Formik
          initialValues={initialValues}
          onSubmit={isUpdateOfferError ? onUpdate : handleSubmit}
          validate={validate}
          validateOnChange={true}
          validateOnBlur={true}
        >
          <Form>
            <Grid spacing={4} container justifyContent="space-between" className={classes.formContainer}>
              {isHomeError && !isFarmPolicy && <Home />}
              {isHomeError && isFarmPolicy && <InitialRiskDetail />}

              {isNoCarsError && <Field type="string" id="VIN" name="VIN" mode="light" label="Vin Number" />}

              {isPriorCoverageError && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Label type="formSubTitleLight">Policy Information</Label>
                  </Grid>

                  <Field
                    type="string"
                    id="global.currentAutoCarrier"
                    name="global.currentAutoCarrier"
                    mode="light"
                    label="Current Carrier"
                    xs={4}
                  />

                  <Field
                    type="date"
                    id="global.continuousAutoCoverageStartDate"
                    name="global.continuousAutoCoverageStartDate"
                    mode="light"
                    label="Auto Coverage Start Date"
                    xs={4}
                  />

                  <Field
                    type="date"
                    id="global.mostRecentAutoInsuranceCoverageEndDate"
                    name="global.mostRecentAutoInsuranceCoverageEndDate"
                    mode="light"
                    label="Coverage Expiration Date"
                    xs={4}
                  />

                  <Grid item xs={12}>
                    <Label type="formSubTitleLight">Auto Policy Coverage</Label>
                  </Grid>

                  <Field
                    type="select"
                    id="global.priorIndividualBILimit"
                    name="global.priorIndividualBILimit"
                    mode="light"
                    label="Prior Bodily Injury Limit (Individual)"
                    options={lookupsJson.priorIndividualBILimit}
                    xs={6}
                  />

                  <Field
                    type="select"
                    id="global.priorOccurrenceBILimit"
                    name="global.priorOccurrenceBILimit"
                    mode="light"
                    label="Prior Bodily Injury Limit (Occurrence)"
                    options={lookupsJson.priorBIOccurrenceLimit}
                    xs={6}
                  />

                  <Field
                    type="select"
                    id="global.priorUmIndividualBodilyInjury"
                    name="global.priorUmIndividualBodilyInjury"
                    mode="light"
                    label="Prior Uninsured Motorist BI (Individual)"
                    options={lookupsJson.priorUmIndividualBodilyInjury}
                    xs={6}
                  />

                  <Field
                    type="select"
                    id="global.priorUmOccurrenceBodilyInjury"
                    name="global.priorUmOccurrenceBodilyInjury"
                    mode="light"
                    label="Prior Uninsured Motorist BI (Occurrence)"
                    options={lookupsJson.priorUmOccurrenceBodilyInjury}
                    xs={6}
                  />
                </Grid>
              )}

              <Grid className={classes.buttonContainer}>
                <Button className={classes.buttons} variant="contained" color="primary" type="submit" loading={loading}>
                  Get price
                </Button>
              </Grid>
            </Grid>
          </Form>
        </Formik>
      </Grid>
    </BaseDialog>
  );
};

ResubmitOfferModal.propTypes = {
  rejectionData: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired
};

ResubmitOfferModal.defaultProps = {
  canOverrideWithoutSaving: false
};

export default observer(withDatePicker(ResubmitOfferModal));
